import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IReport, ReportService } from '@services/report.service';
import { SessionStorageService } from '@services/storage.service';
import { catchError, combineLatest, EMPTY, Observable, retry, shareReplay, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ExternalUrlService } from './externalurl.service';
import { UserService } from './user.service';

export interface IModelMetaData {
	createdAt?: number;
	createdBy?: string;
	updatedAt?: number;
	updatedBy?: string;
	version?: number;
}

export interface IModelBase extends IModelMetaData {
	id?: string;
}

export interface IInsightsUser extends IModelBase {
	externalId: string;
	slideshowInterval: number;
	defaultViewId?: string;
	defaultViewType?: string;
}

export interface IInsightsUserResponse {
	result: IInsightsUser;
}

export interface IInsightsBudgetResponse {
	result: IInsightsBudget;
}

export interface IInsightsBudget extends IModelBase {
	accountId?: string;
	data?: string;
	isDeleted?: boolean;
}

export interface IInsightsCreateBudget {
	data: string;
	name: string;
}

export interface IInsightsUsersResponse {
	result: IInsightsUser[];
}

export interface IInsightsUserResource {
	id?: string;
	externalId: string;
	slideshowInterval: number;
	defaultViewId?: string;
	defaultViewType?: string;
}

export interface IInsightsAccountResponse {
	result: {
		id: string;
		groupIds: string[];
	};
}

export interface IInsightsAccountResource {
	id?: string;
	groupIds: string[];
}

export interface IUploadInsightsBudget {
	sheetName: string;
	data: any;
}

@Injectable({
	providedIn: 'root',
})
export class InsightsService {
	private usersCache = new Map<string, Observable<IInsightsUsersResponse>>();

	constructor(
		private http: HttpClient,
		private userService: UserService,
		private sessionStorageService: SessionStorageService,
		private reportService: ReportService,
		private externalUrlService: ExternalUrlService,
		private router: Router,
	) {
		this.watchAuthIdentityForCacheReset();
	}

	public exitImpersonation(permissions) {
		this.userService.impersonateUser = null;
		this.reportService.fetchUserReports(null, permissions.findIndex((x) => x === 'insights:ReportCreate') > 0);
		combineLatest([this.reportService.fetchUserReportsById(this.sessionStorageService.get('User.IAM.Id')), this.externalUrlService.fetchUserExternalUrlsById(this.sessionStorageService.get('User.IAM.Id'))]).subscribe((data) => {
			if (data[0] && data[1]) {
				const extUrlsReports: IReport[] = data[1].result;
				extUrlsReports.forEach((r) => {
					r.externalId = r.url;
				});
				this.reportService.userReports = [...data[0].result, ...extUrlsReports];
			}
		});
	}

	public addUser(user: IInsightsUserResource): Observable<IInsightsUserResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.post<IInsightsUserResponse>(`${environment.awsEnvUri}/insights/users`, user, {
				headers,
			})
			.pipe(
				retry(1),
				tap(() => this.clearUsersCache(account.id)),
				catchError(this.handleError),
			);
	}

	public updateUser(user: IInsightsUserResource): Observable<IInsightsUserResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.put<IInsightsUserResponse>(`${environment.awsEnvUri}/insights/users`, user, {
				headers,
			})
			.pipe(
				retry(1),
				tap(() => this.clearUsersCache(account.id)),
				catchError(this.handleError),
			);
	}

	public deleteUser(userId: string): Observable<string> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.delete<string>(`${environment.awsEnvUri}/insights/users/${userId}`, { headers }).pipe(
			retry(1),
			tap(() => this.clearUsersCache(account.id)),
			catchError(this.handleError),
		);
	}

	public fetchUsers(): Observable<IInsightsUsersResponse> {
		const account = this.userService.account;
		if (!account?.id) {
			return EMPTY;
		}

		const cacheKey = this.getUsersCacheKey(account.id);
		if (this.usersCache.has(cacheKey)) {
			return this.usersCache.get(cacheKey);
		}

		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		const users$ = this.http
			.get<IInsightsUsersResponse>(`${environment.awsEnvUri}/insights/users`, {
				headers,
			})
			.pipe(
				retry(1),
				catchError((error) => {
					this.usersCache.delete(cacheKey);
					return this.handleError(error);
				}),
				shareReplay(1),
			);

		this.usersCache.set(cacheKey, users$);
		return users$;
	}

	public clearUsersCache(accountId?: string): void {
		if (accountId) {
			this.deleteUsersCacheForAccount(accountId);
			return;
		}

		this.usersCache.clear();
	}

	private deleteUsersCacheForAccount(accountId: string): void {
		for (const cacheKey of this.usersCache.keys()) {
			if (cacheKey.endsWith(`:${accountId}`)) {
				this.usersCache.delete(cacheKey);
			}
		}
	}

	private getUsersCacheKey(accountId: string): string {
		return `${this.getCurrentUserCacheKey()}:${accountId}`;
	}

	private getCurrentUserCacheKey(): string {
		return this.sessionStorageService.get('User.IAM.Id') || this.sessionStorageService.get('User.Email') || this.sessionStorageService.get('User.Authorization') || '';
	}

	private watchAuthIdentityForCacheReset(): void {
		this.sessionStorageService.watch('User.IAM.Id').subscribe(() => this.clearUsersCache());
		this.sessionStorageService.watch('User.Authorization').subscribe(() => this.clearUsersCache());
		this.userService.accountChanged$.subscribe(() => this.clearUsersCache());
	}

	public fetchUserById(): Observable<IInsightsUserResponse> {
		const account = this.userService.account;
		const userId = this.sessionStorageService.get('User.IAM.Id');
		if (!account) {
			return EMPTY;
		}
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};

		return this.http
			.get<IInsightsUserResponse>(`${environment.awsEnvUri}/insights/users/${userId}`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public fetchAccount(): Observable<IInsightsAccountResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http
			.get<IInsightsAccountResponse>(`${environment.awsEnvUri}/insights/accounts`, {
				headers,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	public fetchBudget(): Observable<IInsightsBudgetResponse> {
		const account = this.userService.account;
		console.log(`fetchBudget for account: ${account.id}`);
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.get<IInsightsBudgetResponse>(`${environment.awsEnvUri}/insights/budgets`, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public addBudget(createBudget: IInsightsCreateBudget): Observable<IInsightsBudgetResponse> {
		const account = this.userService.account;
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.post<IInsightsBudgetResponse>(`${environment.awsEnvUri}/insights/budgets`, createBudget, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	public addAccountGroups(groupList: string[]): Observable<IInsightsAccountResponse> {
		const account = this.userService.account;
		const accountGroups: IInsightsAccountResource = {
			groupIds: [],
			id: account.id,
		};
		groupList.forEach((element) => console.log(element));
		groupList.forEach((element) => accountGroups.groupIds.push(element));
		console.log(JSON.stringify(accountGroups));
		const headers = {
			Authorization: `Bearer ${this.sessionStorageService.get('User.Authorization')}`,
			'x-account-id': account.id,
		};
		return this.http.put<IInsightsAccountResponse>(`${environment.awsEnvUri}/insights/accounts`, accountGroups, { headers }).pipe(retry(1), catchError(this.handleError));
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			console.log(errorMessage);
			// window.alert(errorMessage);
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}
